import styled, { keyframes } from "styled-components";

const ease = "cubic-bezier(0.2, 0.7, 0.2, 1)";

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const draw = keyframes`
  from { stroke-dashoffset: 24; }
  to { stroke-dashoffset: 0; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to { opacity: 1; transform: none; }
`;

export const NewsletterWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  background-color: #0043ff;
  background-image: radial-gradient(circle at 100% 0%, rgba(2, 197, 255, 0.35), transparent 55%);
  padding: 44px 48px;
  width: 60%;
  min-width: 760px;
  margin: auto;
  margin-top: -69px;
  margin-bottom: 75px;
  min-height: 140px;
  box-shadow: 0 30px 60px rgba(0, 20, 80, 0.35);
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 18px;
    width: calc(100% - 40px);
    min-width: calc(100% - 40px);
    min-height: 100px;
    margin-top: -50px;
    margin-bottom: 20px;
    padding: 24px 20px;
  }
`;

export const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  @media (max-width: 768px) {
    text-align: center;
  }
`;

export const Eyebrow = styled.span`
  color: #02c5ff;
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

export const Label = styled.div`
  color: #fafbff;
  font-size: 2.17rem;
  line-height: 1.25;
  p {
    margin: 0;
  }
  @media (max-width: 768px) {
    font-size: 1.45rem;
  }
`;

export const Form = styled.form`
  flex: 1;
  max-width: 460px;
  @media (max-width: 768px) {
    max-width: none;
  }
`;

export const Field = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 6px 6px 18px;
  border: 1px solid rgba(250, 251, 255, 0.45);
  border-radius: 999px;
  background: rgba(250, 251, 255, 0.1);
  transition: border-color 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease;
  &:focus-within {
    border-color: #fafbff;
    background: rgba(250, 251, 255, 0.16);
    box-shadow: 0 0 0 4px rgba(250, 251, 255, 0.2);
  }
  &.has-error {
    border-color: #ffd0d6;
    box-shadow: 0 0 0 4px rgba(255, 120, 140, 0.25);
  }
`;

export const MailIcon = styled.svg`
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  fill: none;
  stroke: rgba(250, 251, 255, 0.8);
  stroke-width: 1.6;
  stroke-linejoin: round;
`;

export const Input = styled.input`
  flex: 1;
  min-width: 0;
  padding: 10px 0;
  border: none;
  background: transparent;
  color: #fafbff;
  font: inherit;
  font-size: 1.34rem;
  :focus {
    outline: none;
  }
  ::placeholder {
    color: rgba(250, 251, 255, 0.75);
    opacity: 1;
  }
  @media (max-width: 768px) {
    font-size: 1.15rem;
  }
`;

export const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  padding: 12px 22px;
  border: none;
  border-radius: 999px;
  background: #fafbff;
  color: #0043ff;
  font: inherit;
  font-size: 1.2rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.25s ease, transform 0.25s ${ease};
  svg {
    transition: transform 0.3s ${ease};
  }
  &:hover:not(:disabled) {
    background: #e6edff;
  }
  &:hover:not(:disabled) svg {
    transform: translateX(4px);
  }
  &:active:not(:disabled) {
    transform: scale(0.97);
  }
  &:focus-visible {
    outline: 3px solid #02c5ff;
    outline-offset: 2px;
  }
  &:disabled {
    cursor: progress;
    opacity: 0.85;
  }
  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 1.05rem;
  }
`;

export const Spinner = styled.span`
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

// Reserves its line so a message never pushes the layout around.
export const Status = styled.div`
  min-height: 1.6em;
  margin-top: 10px;
  padding-left: 18px;
  color: #ffe1e6;
  font-size: 1.1rem;
  font-weight: 500;
  animation: ${popIn} 0.3s ease-out;
  @media (max-width: 768px) {
    padding-left: 0;
    text-align: center;
  }
`;

export const Success = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
  max-width: 460px;
  color: #fafbff;
  font-size: 1.34rem;
  line-height: 1.4;
  animation: ${popIn} 0.45s ${ease};
  @media (max-width: 768px) {
    max-width: none;
    font-size: 1.15rem;
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const Check = styled.svg`
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  padding: 10px;
  border-radius: 50%;
  background: #fafbff;
  fill: none;
  stroke: #0043ff;
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-linejoin: round;
  path {
    stroke-dasharray: 24;
    animation: ${draw} 0.5s ${ease} 0.2s both;
  }
  @media (prefers-reduced-motion: reduce) {
    path {
      animation: none;
    }
  }
`;
